import { useAuthStore } from '@/lib/core/stores';
import { getCurrentUserV1 } from '../../api/authentication/authentication';
import {
  deleteMeAccountV1,
  getMeMembershipAutoRenewalV1,
  getMeMembershipV1,
  getMeV1,
  requestMeMembershipUpgradeV1,
  setMeMembershipAutoRenewalV1,
  updateMeProfileV1,
  uploadMePhotoV1,
} from '../../api/me-—-profile/me-—-profile';
import {
  UserDto,
  MembershipDto,
  MembershipAutoRenewalDto,
  MembershipUpgradeRequestDto,
  SetMembershipAutoRenewalRequest,
  UpdateProfileRequest,
  UploadPhotoRequest,
  AppSettingsContentResponseModel,
  SalutationContentResponseModel,
  SalutationContentResponseModelContentType,
  CountryContentResponseModel,
  CountryContentResponseModelContentType,
  MeDto,
} from '../../api/model';
import { getContents } from '../cms';

/**
 * Fetches the currently authenticated user's profile.
 *
 * Reads the access token from `useAuthStore`. Returns `undefined` without
 * making a network call if no token is present.
 *
 * @returns `UserDto` on HTTP 200.
 * @returns `undefined` if unauthenticated, on non-200 response, or on network error.
 */
export async function getCurrentUser(): Promise<UserDto | undefined> {
  try {
    const accessToken = useAuthStore.getState().accessToken;

    if (!accessToken) {
      return undefined;
    }

    const response = await getCurrentUserV1({
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.status === 200) {
      return response.data as UserDto;
    } else {
      console.warn(`Error fetching current user (status: ${response.status})`);
      return undefined;
    }
  } catch (error) {
    console.warn('Error fetching current user:', error);
    return undefined;
  }
}

/**
 * Fetches the active membership for the authenticated user.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @returns `MembershipDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getMembership(): Promise<MembershipDto | undefined> {
  try {
    const response = await getMeMembershipV1();

    if (response.status === 200) {
      return response.data as MembershipDto;
    }

    console.warn(`getMembership: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMembership: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches the automatic membership renewal state for the authenticated user.
 *
 * Reflects what the "Allow automatic membership renewal" switch should show:
 * whether it is currently on, the amount and currency that would be charged
 * and when, and whether the member has a payment method on file. A `false`
 * `hasPaymentMethod` is why the switch cannot be turned on — route the member
 * to add a card first rather than letting them toggle into an error.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @returns `MembershipAutoRenewalDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (profile not found), or network error.
 */
export async function getMembershipAutoRenewal(): Promise<MembershipAutoRenewalDto | undefined> {
  try {
    const response = await getMeMembershipAutoRenewalV1();

    if (response.status === 200) {
      return response.data as MembershipAutoRenewalDto;
    }

    console.warn(`getMembershipAutoRenewal: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getMembershipAutoRenewal: request failed:', error);
    return undefined;
  }
}

/**
 * Turns automatic membership renewal on or off for the authenticated user.
 *
 * Turning it ON creates a Stripe subscription that charges the membership quota on its
 * renewal date, using the member's saved card. Nothing is charged immediately. Turning it
 * OFF cancels that subscription immediately — since nothing has been charged yet, there is
 * no paid period to see out.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param request - `{ enabled }` — the desired auto-renewal state.
 * @returns The updated `MembershipAutoRenewalDto` on HTTP 200.
 * @returns `undefined` on:
 * - 401 (unauthenticated)
 * - 404 `membership_not_found` — no active membership to renew
 * - 409 `membership_payment_method_required` — no saved card; send the member to add one
 * - 422 `membership_quota_unavailable` — the membership carries no quota to charge
 * - 422 `membership_renewal_date_unavailable` — no renewal date, or it has already passed
 * - 503 `membership_subscription_failed` — Stripe refused; transient, worth retrying
 * - network error
 */
export async function setMembershipAutoRenewal(
  request: SetMembershipAutoRenewalRequest
): Promise<MembershipAutoRenewalDto | undefined> {
  try {
    const response = await setMeMembershipAutoRenewalV1(request);

    if (response.status === 200) {
      return response.data as MembershipAutoRenewalDto;
    }

    console.warn(`setMembershipAutoRenewal: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('setMembershipAutoRenewal: request failed:', error);
    return undefined;
  }
}

/**
 * Submits a request to upgrade the authenticated user's membership.
 *
 * Creates an Opportunity in Salesforce for Customer Service to pick up. Nothing is
 * charged — the tier, price, and payment are all settled with the member afterwards,
 * outside the app. Takes no body: the requested tier is derived server-side from the
 * one the member currently holds (the next one up), since the screen is a confirmation
 * and offers no choice. A member already at the top tier still gets a request through.
 *
 * Requires a caller-generated `Idempotency-Key` (a fresh UUID per attempt). Salesforce
 * cannot de-duplicate Opportunities on its own, so a double tap would otherwise leave two
 * for someone to close by hand — repeating the same key instead replays the first answer.
 * Reuse the same key only to retry one attempt; generate a new one for a genuinely new
 * request.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param idempotencyKey - Caller-generated UUID identifying this attempt.
 * @returns `MembershipUpgradeRequestDto` on HTTP 200.
 * @returns `undefined` on:
 * - 400 — the `Idempotency-Key` header is missing
 * - 401 (unauthenticated)
 * - 422 `membership_upgrade_refused` — Salesforce rejected it; repeating changes nothing
 * - 503 `membership_upgrade_failed` — could not be delivered; nothing was created, safe to
 *   retry with a NEW key
 * - network error
 */
export async function requestMembershipUpgrade(
  idempotencyKey: string
): Promise<MembershipUpgradeRequestDto | undefined> {
  try {
    const response = await requestMeMembershipUpgradeV1({
      headers: { 'Idempotency-Key': idempotencyKey },
    });

    if (response.status === 200) {
      return response.data as MembershipUpgradeRequestDto;
    }

    console.warn(`requestMembershipUpgrade: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('requestMembershipUpgrade: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches the full account profile for the authenticated user.
 *
 * Returns the CRM-level `MeDto` which includes personal details,
 * contact information, billing/shipping addresses, and membership identifiers.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @returns `MeDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (profile not found), or network error.
 */
export async function getUserProfile(): Promise<MeDto | undefined> {
  try {
    const response = await getMeV1();

    if (response.status === 200) {
      return response.data as MeDto;
    }

    console.warn(`getUserProfile: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getUserProfile: request failed:', error);
    return undefined;
  }
}

/**
 * Updates whitelisted profile fields for the authenticated user.
 *
 * Sends a PATCH to `/api/v1/me/profile`. All fields in `UpdateProfileRequest`
 * are optional — only the provided fields are updated. Changes propagate to
 * MuleSoft; `otherPhone` is also persisted locally on the backend.
 *
 * Updatable fields: `salutation`, `firstName`, `lastName`, `birthDate`,
 * `otherEmail`, `otherPhone`, `countryOfResidenceName`, `countryOfResidenceISO`.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param request - Partial profile fields to update.
 * @returns The updated `MeDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (profile not found), or network error.
 */
export async function updateProfile(request: UpdateProfileRequest): Promise<MeDto | undefined> {
  try {
    const response = await updateMeProfileV1(request);

    if (response.status === 200) {
      return response.data as MeDto;
    }

    console.warn(`updateProfile: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('updateProfile: request failed:', error);
    return undefined;
  }
}

/**
 * Uploads a base64-encoded profile photo for the authenticated user.
 *
 * Sends a POST to `/api/v1/me/photo`. The image must be provided as a
 * base64 string along with its MIME content type (e.g. `"image/jpeg"`).
 * Returns the full updated account profile on success.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param request - `{ imageBase64, contentType }` — base64-encoded image and its MIME type.
 * @returns The updated `MeDto` on HTTP 200.
 * @returns `undefined` on 400 (invalid payload), 401 (unauthenticated), 404 (profile not found), or network error.
 */
export async function uploadProfilePhoto(request: UploadPhotoRequest): Promise<MeDto | undefined> {
  try {
    const response = await uploadMePhotoV1(request);

    if (response.status === 200) {
      return response.data as MeDto;
    }

    console.warn(`uploadProfilePhoto: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('uploadProfilePhoto: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches all salutation options from the CMS.
 *
 * Queries the children of the `salutationRepository` node defined in `appSettings`.
 * Returns every item cast to `SalutationContentResponseModel` — ordered as the CMS
 * returns them (typically alphabetical or editorial order).
 *
 * @param appSettings - App settings page, used to read `salutationRepository.id`.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`) forwarded as `Accept-Language`.
 * @returns Array of `SalutationContentResponseModel` on success.
 * @returns `undefined` when the repository ID is absent, on non-2xx response, or on network error.
 */
export async function getProfileSalutations(
  appSettings: AppSettingsContentResponseModel,
  culture?: string
): Promise<SalutationContentResponseModel[] | undefined> {
  try {
    const repositoryId = appSettings.properties?.salutationRepository?.id;

    if (!repositoryId) {
      console.warn('getProfileSalutations: salutationRepository id is missing from appSettings');
      return undefined;
    }

    const result = await getContents(
      {
        fetch: `children:${repositoryId}`,
        filter: [`contentType:${SalutationContentResponseModelContentType.salutation}`],
        skip: 0,
        take: 50,
        sort: ['name:asc', 'sortOrder:asc'],
      },
      culture
    );

    return (result?.items ?? []) as SalutationContentResponseModel[];
  } catch (error) {
    console.warn('getProfileSalutations: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches all country options from the CMS.
 *
 * Queries the children of the `countryRepository` node defined in `appSettings`.
 * Returns every item cast to `CountryContentResponseModel` — ordered as the CMS
 * returns them (typically alphabetical or editorial order).
 *
 * @param appSettings - App settings page, used to read `countryRepository.id`.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`) forwarded as `Accept-Language`.
 * @returns Array of `CountryContentResponseModel` on success.
 * @returns `undefined` when the repository ID is absent, on non-2xx response, or on network error.
 */
export async function getProfileCountries(
  appSettings: AppSettingsContentResponseModel,
  culture?: string
): Promise<CountryContentResponseModel[] | undefined> {
  try {
    const repositoryId = appSettings.properties?.countryRepository?.id;

    if (!repositoryId) {
      console.warn('getProfileCountries: countryRepository id is missing from appSettings');
      return undefined;
    }

    const result = await getContents(
      {
        fetch: `children:${repositoryId}`,
        filter: [`contentType:${CountryContentResponseModelContentType.country}`],
        skip: 0,
        take: 250,
        sort: ['name:asc', 'sortOrder:asc'],
      },
      culture
    );

    return (result?.items ?? []) as CountryContentResponseModel[];
  } catch (error) {
    console.warn('getProfileCountries: request failed:', error);
    return undefined;
  }
}

/**
 * Soft-deletes and anonymizes the authenticated user's local account.
 *
 * Clears the Entra ID reference on Salesforce (the CRM record is preserved),
 * then deletes the Entra user. This operation is **irreversible**. Failures in
 * external systems (Salesforce, Entra) do not block local erasure — they are
 * flagged for retry by the API.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `true` on HTTP 204.
 * @returns `false` on 401 (unauthenticated), 404 (account not found), or network error.
 */
export async function deleteProfileAccount(language?: string): Promise<boolean> {
  try {
    const response = await deleteMeAccountV1({ headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 204) {
      return true;
    }

    console.warn(`deleteProfileAccount: unexpected status ${response.status}`);
    return false;
  } catch (error) {
    console.warn('deleteProfileAccount: request failed:', error);
    return false;
  }
}
